import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { statusOptions, statusLabels } from './types';
import type { Schema } from '../../services/api';

export interface CreateSchemaSetDialogProps {
  open: boolean;
  onClose: () => void;
  createForm: { name: string; status: string; tenantId: string; createdBy: string };
  onFormChange: React.Dispatch<React.SetStateAction<{ name: string; status: string; tenantId: string; createdBy: string }>>;
  onSubmit: () => void;
  isLoading: boolean;
  existingSchemas?: Schema[];
  /** @deprecated Use existingSchemas instead */
  existingSchemaSets?: Schema[];
}

const SCHEMA_NAME_MAX_LENGTH = 255;

const CreateSchemaSetDialog: React.FC<CreateSchemaSetDialogProps> = ({
  open,
  onClose,
  createForm,
  onFormChange,
  onSubmit,
  isLoading,
  existingSchemas,
  existingSchemaSets,
}) => {
  const allSchemas = existingSchemas ?? existingSchemaSets ?? [];
  const isNameOverLimit = createForm.name.length > SCHEMA_NAME_MAX_LENGTH;
  const isDuplicateName = allSchemas.some(
    (s) => s.schema_name.toLowerCase() === createForm.name.trim().toLowerCase(),
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Schema</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Name"
            fullWidth
            required
            value={createForm.name}
            onChange={(event) =>
              onFormChange((prev) => ({ ...prev, name: event.target.value }))
            }
            size="small"
            inputProps={{ maxLength: SCHEMA_NAME_MAX_LENGTH }}
            error={isNameOverLimit || isDuplicateName}
            helperText={
              isNameOverLimit
                ? `Name must be at most ${SCHEMA_NAME_MAX_LENGTH} characters.`
                : isDuplicateName
                ? 'A schema with this name already exists.'
                : createForm.name.length > 0
                ? `${createForm.name.length}/${SCHEMA_NAME_MAX_LENGTH}`
                : undefined
            }
          />
          <TextField
            label="Status"
            fullWidth
            select
            value={createForm.status}
            onChange={(event) => onFormChange((prev) => ({ ...prev, status: event.target.value }))}
            size="small"
          >
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {statusLabels[status] ?? status}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isLoading || !createForm.name.trim() || isNameOverLimit || isDuplicateName}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSchemaSetDialog;
